/**
 * Tasks (FR-TSK-01 to FR-TSK-04).
 *
 * A note with an optional amount and an optional due date. That is deliberately almost
 * nothing: FR-TSK-03 says tasks generalise the workbook's commitments block **and** the
 * loose column-F note lists, and those two are only alike in being "things written down".
 * Requiring an amount would exclude half of them; requiring a date would exclude most.
 *
 * The one piece of behaviour worth having is FR-TSK-02: **a task carrying an amount
 * converts to a transaction in one tap**, pre-filled and still editable. That is the
 * difference between a to-do list and a to-do list that is part of the books.
 */

import type { DateKey } from './dates';
import type { Paise } from './money';
import type { TransactionInput } from './transactions';

export interface Task {
  id: string;
  text: string;
  amountPaise: Paise | null;
  dueDateKey: DateKey | null;
  done: boolean;
  /** Manual order (FR-TSK-04). Sparse, so a reorder rarely rewrites its neighbours. */
  sortOrder: number;
  /** Set when the task became a transaction, so it cannot be posted twice. */
  transactionId: string | null;
  createdFrom?: 'workbook' | 'manual';
  note?: string | null;
}

export class TaskError extends Error {}

export function validateTask(input: Pick<Task, 'text' | 'amountPaise'>): string | null {
  if (!input.text.trim()) return 'A task needs some text.';
  if (input.amountPaise !== null && !Number.isSafeInteger(input.amountPaise)) {
    return 'That amount could not be read.';
  }
  return null;
}

/**
 * Ordering.
 *
 * Outstanding before done; then anything dated, soonest first; then the undated by their
 * manual order. Dated things sort ahead of undated ones because a date is a commitment
 * and a bare note is not — an undated "Solar panel" should not sit above a bill due
 * tomorrow just because it was typed first.
 *
 * Overdue items need no special case: they carry an earlier date, so they are already at
 * the top. `isOverdue` exists for how a row is *drawn*, not for where it sits.
 */
export function sortTasks(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;

    if (a.dueDateKey && b.dueDateKey) {
      if (a.dueDateKey !== b.dueDateKey) return a.dueDateKey.localeCompare(b.dueDateKey);
    } else if (a.dueDateKey) return -1;
    else if (b.dueDateKey) return 1;

    return a.sortOrder - b.sortOrder || a.text.localeCompare(b.text);
  });
}

export function isOverdue(task: Task, today: DateKey): boolean {
  return !task.done && task.dueDateKey !== null && task.dueDateKey < today;
}

export function outstanding(tasks: Task[]): Task[] {
  return tasks.filter((task) => !task.done);
}

/** Tasks that can become transactions — the ones FR-TSK-02 is about. */
export function convertible(tasks: Task[]): Task[] {
  return tasks.filter((task) => task.amountPaise !== null && task.transactionId === null && !task.done);
}

export function totalOutstandingPaise(tasks: Task[]): Paise {
  return outstanding(tasks).reduce((sum, task) => sum + (task.amountPaise ?? 0), 0);
}

/**
 * A task, pre-filled as a transaction (FR-TSK-02).
 *
 * Everything is a default the form may overwrite. In particular the amount: a task is an
 * intention written down in advance, so the figure on it is an estimate for exactly the
 * same reason a recurring item's is (FR-REC-03), and it is wrong about as often.
 */
export function toTransactionInput(
  task: Task,
  defaults: { accountId: string; dateKey: DateKey },
): TransactionInput {
  if (task.amountPaise === null) {
    throw new TaskError('That task carries no amount, so there is nothing to post.');
  }
  if (task.transactionId !== null) {
    throw new TaskError('That task has already been posted.');
  }

  return {
    type: 'expense',
    dateKey: task.dueDateKey ?? defaults.dateKey,
    accountId: defaults.accountId,
    amountPaise: task.amountPaise,
    description: task.text.trim(),
    categoryId: null,
  };
}

/**
 * A sort order that places a task at the end.
 *
 * Stepped by 1,000 so a later drag can land between two neighbours without renumbering
 * the list — the alternative rewrites every task after the insertion point, which on a
 * thirty-item list is thirty writes for one drag.
 */
export function nextSortOrder(tasks: Task[]): number {
  return tasks.reduce((max, task) => Math.max(max, task.sortOrder), 0) + 1000;
}

/**
 * The order value for a task dropped between two others.
 *
 * Returns null when there is no room left between them, which tells the caller to
 * renumber rather than silently producing a duplicate order.
 */
export function orderBetween(before: Task | null, after: Task | null): number | null {
  const low = before?.sortOrder ?? 0;
  const high = after?.sortOrder ?? low + 2000;
  if (high - low < 2) return null;
  return Math.floor((low + high) / 2);
}

/** Renumber cleanly, for when `orderBetween` runs out of room. */
export function renumber(tasks: Task[]): Task[] {
  return tasks.map((task, index) => ({ ...task, sortOrder: (index + 1) * 1000 }));
}
